// VH-67: Renders findings.ai_narrative with on-demand translation when language != en.
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { SparklesIcon, LanguageIcon } from '@heroicons/react/24/outline'
import { reportsAPI } from '../../services/api'
import { contentLanguage } from '../../i18n/supported'

export default function AiNarrative({ reportId, narrative, unavailable }) {
  const { t, i18n } = useTranslation()
  const [translatedText, setTranslatedText] = useState(null)

  const lang = contentLanguage(i18n?.language)
  const canTranslate = lang !== 'en' && !!reportId && !!narrative

  const translateMutation = useMutation({
    mutationFn: () => reportsAPI.translateReport(reportId, lang),
    onSuccess: (response) => {
      const data = response?.data
      const next = data?.ai_narrative || data?.translated || data?.narrative || null
      if (next) setTranslatedText(next)
    },
  })

  if (unavailable && !narrative) {
    return (
      <div className="card border-l-4 border-yellow-400">
        <div className="card-body">
          <div className="flex items-center gap-2 mb-1">
            <SparklesIcon className="h-5 w-5 text-yellow-500" />
            <h3 className="text-lg font-semibold text-gray-900">{t('reports.ai.narrative')}</h3>
          </div>
          <p className="text-sm text-gray-600">{t('reports.ai.unavailable')}</p>
        </div>
      </div>
    )
  }

  if (!narrative) return null

  const display = translatedText || narrative

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-center gap-2 mb-2">
          <SparklesIcon className="h-5 w-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">{t('reports.ai.narrative')}</h3>
          {canTranslate && !translatedText && (
            <button
              type="button"
              className="ml-auto text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 disabled:opacity-50"
              onClick={() => translateMutation.mutate()}
              disabled={translateMutation.isPending}
            >
              <LanguageIcon className="h-4 w-4" />
              {translateMutation.isPending
                ? t('reports.ai.translating')
                : t('reports.ai.translate')}
            </button>
          )}
        </div>
        <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{display}</p>
      </div>
    </div>
  )
}
